/**
 * Editor load-flow cluster — file-load orchestration, loading-overlay
 * helpers, and Tier-1/Tier-2 lifecycle dispatch.
 *
 * Extracted from editor.ts in Phase EB.6 of the editor-ts-decompose Epic
 * (design doc: docs/design/editor-ts-decompose-plan-260522.md §"Phase EB.6"
 * + §3 Cluster C).
 *
 * BACKEND-DRIVEN LOAD (pdf-editor-no-itext Phase 3+4, retrofit design
 * docs/design/pdf-editor-backend-session-retrofit-260715.md §2.2/§3): the
 * AUTHORITATIVE struct-tree the editor renders + edits (`state.ir`) AND the
 * accessibility attestation (`state.attestation`) come from the BACKEND
 * `editor-ir` endpoint (Phase 2 enriched its meta with per-block bboxes +
 * attestation). Every editor entry is jobId-keyed: a direct-upload entry
 * (drag-drop / picker) registers a transient job up front (§G-Q1), so there is
 * no jobId-less path. The in-browser WASM+iText parser was deleted in Phase 4;
 * `state.interop` is now purely a backend/IR-derived seam whose failure is
 * NON-FATAL when the backend IR loaded, and FATAL only when the backend is
 * unreachable (no client-side parser to fall back to).
 *
 * What lives here:
 *   - `loadFile(file, opts, applyLayout)`: top-level orchestrator for the
 *     PDF load — registers a transient job when needed, wires file persistence,
 *     the backend-IR fetch (authoritative struct-tree + attestation), the
 *     IR-derived overlay geometry, pdf.js bootstrap, page-1 render, and
 *     schedules Tier-2 background fill.
 *   - `_scheduleTier2Loading(t0)`: Tier-2 background load (IR fetch, IDB
 *     reads, oplog replay, card-pane + screen-reader mount, overlay
 *     re-render).
 *   - `withTimeout`: race-with-timeout helper used by the struct-parse path.
 *   - `showLoading` / `setLoadingLabel` / `showLoadingError` / `hideLoading`:
 *     LoadingOverlayController routing — the four functions that flip the
 *     overlay between LOADING / HIDING / HIDDEN / ERROR atomically.
 *   - `_loadingOverlay` singleton: lazy-init on first call to a helper
 *     above (els.* must exist before the controller is constructed).
 *   - `_resetCardPaneOverlay` / `_getCardPaneOverlay`: a SECOND, PANE-LOCAL
 *     `LoadingOverlayController` scoped to `els.paneEditor` only, driven by
 *     `_scheduleTier2Loading` (Phase 5, editor-pdf-item-editing-260910
 *     §3.3) — closes the "looked stuck" gap between Tier-1's `hideLoading()`
 *     and the card pane actually rendering, without locking the already-
 *     usable Preview pane the way the page-global overlay would.
 *
 * Cross-cluster coupling:
 *
 *   `loadFile` and `_scheduleTier2Loading` call into Cluster F
 *   (card pane / op dispatch) and Cluster H (screen-reader / attestation)
 *   surfaces, now extracted to their own modules:
 *     - `mountCardPane()` — Cluster F (extracted in EB.5 → editor-card-pane.ts)
 *     - `mountScreenReaderPanel()` — Cluster H (extracted in EB.7 → editor-modals-save.ts)
 *     - `showEditorToast(message, type)` — Cluster F (EB.5 → editor-card-pane.ts)
 *     - `populateSidebar(selectStruct)` — Cluster H (EB.7 → editor-modals-save.ts)
 *     - `updateAttestationPill()` — Cluster H (EB.7 → editor-modals-save.ts)
 *
 *   All five are direct-imported from their home modules. No editor.ts
 *   import remains in this file after Phase EB.7.
 *
 *   `applyLayout` is the orchestrator-owned pane-layout callback. It's
 *   passed as a parameter to `loadFile` rather than imported, mirroring
 *   the pattern `editor-empty-state.showWorkspace(applyLayout)` already
 *   uses for the same reason — pane-layout is a singleton wired by the
 *   orchestrator and shouldn't be a global module-scope import.
 *
 * `state` and `els` come from `editor-state.js`. `_scheduleIdle` comes
 * from `editor-render.js` (extracted in Phase EB.2; this module imports
 * it rather than re-declaring). `announce`, `showWorkspace` come from
 * `editor-empty-state.js` (EB.1).
 *
 * Per the "Type-in-place before decomposing" rule (CLAUDE.md): the
 * existing `any` annotations on `state.interop`, `state.pdfDoc`,
 * `state.attestation` are preserved — tightening them is the
 * `ts-strict-zero` Epic's job.
 */
import { log } from "./log.js";
import { createEditorInterop } from "./interop.js";
import { registerTransientJob } from "./editor-transient.js";
import { buildIRFromStructTree } from "./ir.js";
import { rebuildBlocksByPage, replaceIR, dispatch } from "./dispatcher.js";
import { OpLog } from "./oplog.js";
import { storeOpenFile } from "./session-store.js";
import { loadLocalOps, loadIrSnapshot, storeIrSnapshot, parseJsonlBody, mergeServerOplog, stripPersistenceFields, } from "./idb-oplog.js";
import { appendTokenIfPresent } from "./url-token.js";
import { loadAuthoritativeIR } from "./editor-ir-fetch.js";
import { EditorLifecycleState } from "./editor-lifecycle-state.js";
import { LoadingOverlayState, LoadingOverlayController, } from "./loading-overlay-state.js";
import { state, els, prefetchRange, } from "./editor-state.js";
import { showWorkspace } from "./editor-empty-state.js";
import { announce } from "../a11y-view/announce.js";
import { renderPage, renderOverlays, _scheduleIdle, } from "./editor-render.js";
import { mountMultiPageScroll, updatePageNavControls, } from "./editor-multipage.js";
// Cluster F (mountCardPane, showEditorToast) extracted in Phase EB.5.
// Cluster H (mountScreenReaderPanel, populateSidebar, updateAttestationPill)
// extracted in Phase EB.7. All direct-imported from their home modules.
import { mountCardPane, showEditorToast } from "./editor-card-pane.js";
import { mountScreenReaderPanel, populateSidebar, updateAttestationPill, } from "./editor-modals-save.js";
// ─── Loading overlay helpers (issue 6) ────────────────────────────────────
// Timeout for the struct-tree derive / backend-IR seam. The IR-derived seam is
// near-instant; this ceiling mostly guards the backend `editor-ir` fetch. Beyond
// it we surface a user-visible error rather than spinning forever.
const STRUCT_PARSE_TIMEOUT_MS = 60_000;
/**
 * Race a promise against a timeout. On timeout, rejects with a named error
 * so callers can distinguish timeout from other failures.
 */
export async function withTimeout(promise, ms, label) {
    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
            reject(Object.assign(new Error(`${label} timed out after ${ms / 1000}s`), { isTimeout: true }));
        }, ms);
        // eslint-disable-next-line promise/prefer-await-to-then -- Promise constructor executor cannot use await; .then() is the only resolver wiring available here
        promise.then((v) => { clearTimeout(timer); resolve(v); }, 
        // eslint-disable-next-line @typescript-eslint/prefer-promise-reject-errors -- propagating the upstream rejection reason as-is; re-wrapping would hide original cause
        (e) => { clearTimeout(timer); reject(e); });
    });
}
// Singleton loading-overlay controller. Lazy-initialised on first use so
// `els.loadingOverlay` / `els.loadingLabel` are guaranteed populated by the
// time the controller is constructed. All four functions below route
// through it so class assignments are atomic — non-atomic class flips
// were observed leaving the spinner red on retry (showLoading after
// showLoadingError must explicitly clear `is-error`). Pinned by
// test/loading-overlay-state.test.mjs.
let _loadingOverlay = null;
function _getLoadingOverlay() {
    _loadingOverlay ??= new LoadingOverlayController({
        overlayEl: els.loadingOverlay,
        labelEl: els.loadingLabel,
    });
    return _loadingOverlay;
}
export function showLoading(label) {
    // Legal source states: HIDDEN (boot, post-hide), HIDING (mid-fadeout),
    // ERROR (retry-after-failure), or LOADING itself (re-issue with new label).
    _getLoadingOverlay().setState(LoadingOverlayState.LOADING, { label });
}
export function setLoadingLabel(label) {
    _getLoadingOverlay().setLabel(label);
}
/**
 * Transition the loading overlay to an error state. The spinner continues
 * to show but the label surfaces the error message, and the overlay stays
 * visible until the user acts (it won't auto-dismiss). Adds a "Try again"
 * button if one is not already present.
 */
export function showLoadingError(message) {
    _getLoadingOverlay().setState(LoadingOverlayState.ERROR, { label: message });
    // Offer a "Try again" action by reloading — keeps the UX from being a dead end.
    if (!els.loadingOverlay.querySelector(".loading-retry-btn")) {
        const btn = document.createElement("button"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        btn.className = "editor-btn loading-retry-btn";
        btn.textContent = "Try a different file";
        btn.setAttribute("type", "button");
        btn.addEventListener("click", () => {
            hideLoading();
            btn.remove();
        });
        const content = els.loadingOverlay.querySelector(".editor-loading-content");
        if (content)
            content.appendChild(btn);
    }
}
export function hideLoading() {
    const overlay = _getLoadingOverlay();
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches; // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    if (prefersReduced) {
        // ERROR state can also reach HIDDEN directly; LOADING is the
        // common case. Both transitions are legal in the state table.
        overlay.setState(LoadingOverlayState.HIDDEN);
        return;
    }
    // ERROR → HIDDEN is legal but we still want a fadeout when reachable;
    // ERROR is rare and the user-visible delta (fade vs snap) is small,
    // so route ERROR through HIDDEN directly to avoid an illegal
    // ERROR → HIDING transition.
    if (overlay.is(LoadingOverlayState.ERROR)) {
        overlay.setState(LoadingOverlayState.HIDDEN);
        return;
    }
    // Fade out, then fully hide.
    overlay.setState(LoadingOverlayState.HIDING);
    els.loadingOverlay.addEventListener("transitionend", () => {
        // Guard: a re-issued showLoading() during the fadeout would have
        // transitioned us out of HIDING — don't snap back to HIDDEN.
        if (overlay.is(LoadingOverlayState.HIDING)) {
            overlay.setState(LoadingOverlayState.HIDDEN);
        }
    }, { once: true });
}
const PANE_LOADING_LABELS = {
    "fetch-oplog": "Fetching edit history…",
    "replay-ops": "Applying edits…",
    "build-cards": "Building item list…",
};
const CARD_PANE_OVERLAY_CLASS = "editor-card-pane-loading-overlay";
const CARD_PANE_OVERLAY_LABEL_CLASS = "editor-card-pane-loading-label";
/**
 * Lazily creates (or reuses) the pane-scoped overlay element as a child of
 * `els.paneEditor` ONLY — never `els.panePreview` — so the PDF canvas stays
 * interactive throughout Tier-2 (P-7/P-8). Layout/background/stacking are
 * styled via the `.editor-card-pane-loading-overlay` CSS class in editor.css,
 * NOT inline styles — the `LoadingOverlayController` hides the overlay by
 * adding the `.hidden` class (`display:none`), and an inline `display:flex`
 * would WIN over the class (inline beats a non-`!important` class selector),
 * leaving the overlay permanently visible on its last label ("Building item
 * list…") the moment Tier-2 finished. That regression — a stuck pane overlay
 * whose 85%-opaque-white background made the card list look unpopulated — is
 * exactly why the display rule must live in CSS, mirroring the page-global
 * `.editor-loading-overlay` (docable-editor-liststuck-emptypara-261001).
 */
function _buildCardPaneOverlayEl() {
    const existing = els.paneEditor.querySelector(`.${CARD_PANE_OVERLAY_CLASS}`);
    if (existing)
        return existing;
    // WHY inline position:relative: an absolutely-positioned overlay child
    // sizes/positions against the nearest POSITIONED ancestor. Without this,
    // the overlay would size against a higher, unrelated layout root instead
    // of just the Edit pane. (This is on `paneEditor`, not the overlay, so it
    // does not conflict with the overlay's `.hidden` display toggle.)
    if (!els.paneEditor.style.position) {
        els.paneEditor.style.position = "relative";
    }
    const overlay = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    overlay.className = `${CARD_PANE_OVERLAY_CLASS} hidden`;
    overlay.setAttribute("role", "status");
    overlay.setAttribute("aria-live", "polite");
    const label = document.createElement("span"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    label.className = CARD_PANE_OVERLAY_LABEL_CLASS;
    overlay.appendChild(label);
    els.paneEditor.appendChild(overlay);
    return overlay;
}
let _cardPaneOverlay = null;
/**
 * (Re)constructs the pane-local Tier-2 controller in a fresh HIDDEN state.
 * Called once at the top of every `_scheduleTier2Loading` run — "created at
 * mount, HIDDEN initial" (design §3.3.2). A stale generation's chain never
 * calls `setState` on its own captured reference before checking
 * `state.lifecycle.generation` (see call sites below), so a superseded
 * Tier-2 run can't leak a leftover LOADING class onto a NEW document's pane
 * overlay — the fresh reset here is what makes that guarantee hold even
 * though the underlying DOM node is reused across loads (§3.3.4a).
 */
export function _resetCardPaneOverlay() {
    const el = _buildCardPaneOverlayEl();
    el.classList.remove("is-hiding", "is-error");
    el.classList.add("hidden");
    _cardPaneOverlay = new LoadingOverlayController({
        overlayEl: el,
        labelEl: el.querySelector(`.${CARD_PANE_OVERLAY_LABEL_CLASS}`),
        initial: LoadingOverlayState.HIDDEN,
    });
    return _cardPaneOverlay;
}
/**
 * Test/inspection seam — the live pane-local Tier-2 controller, or null
 * before the first `_scheduleTier2Loading` run of this page session.
 */
export function _getCardPaneOverlay() {
    return _cardPaneOverlay;
}
// ─── Load flow ─────────────────────────────────────────────────────────────
/**
 * Top-level file-load orchestrator. Ensures a jobId (registering a transient
 * job for direct-upload entries), fetches the authoritative backend IR, primes
 * pdf.js for page-1 render, then transitions through Tier-1 + Tier-2 lifecycle
 * states.
 *
 * @param file The PDF file to load.
 * @param opts.skipPersist Set true to skip the IDB write of the file bytes
 *   (used when restoring from the back-button — we already have the bytes
 *   in IDB and don't want to round-trip them).
 * @param applyLayout The orchestrator-owned pane-layout apply callback;
 *   passed into `showWorkspace` to size the layout once the workspace
 *   becomes visible.
 */
export async function loadFile(file, opts = {}, applyLayout, cardPaneDeps) {
    const t0 = performance.now();
    announce(`Loading ${file.name}`);
    els.fileName.textContent = file.name;
    els.fileName.title = file.name;
    state.fileName = file.name;
    // Mirror the open filename into <title> so AT users (and people with
    // multiple tabs open) can tell which document each tab holds without
    // visiting the page. Without this, every editor tab is "DocAble Editor"
    // in the tab bar and the screen-reader's page-title announcement.
    document.title = `${file.name} — DocAble Editor`; // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    // R3 (editor-open-load-state-coherence-260813 Phase 2): loadFile is the
    // "load into a CLEAN editor" primitive — it must be entered only from
    // EMPTY_COLD (boot) or EMPTY_WARM (post-reset). Document SWITCHES route
    // through `loadDocument` (editor.ts), which resets to EMPTY_WARM first. A
    // non-empty source here means a caller bypassed `loadDocument` and would
    // silently reuse the prior document's jobId/interop (BUG #5) — fail loud at
    // this seam rather than deep in the SM. (Pre-Phase-2 this admitted a
    // LOADED_FULL "user dropped a 2nd file" source; that edge was removed.)
    if (!state.lifecycle.isAny([
        EditorLifecycleState.EMPTY_COLD,
        EditorLifecycleState.EMPTY_WARM,
    ])) {
        throw new Error(`[editor] loadFile must be entered from EMPTY_COLD/EMPTY_WARM `
            + `(got ${state.lifecycle.state}); route document switches through loadDocument.`);
    }
    state.lifecycle.transition(EditorLifecycleState.LOADING);
    // Ensure a jobId. A pipeline entry arrives with `?jobId=…` (set on
    // `state.jobId` at boot). A direct-upload entry (drag-drop / picker / ?url=
    // fetch / session restore) has none, so register the bytes as a lightweight
    // transient job (`POST /api/editor-transient`, retrofit design §G-Q1) and key
    // the whole backend editor plumbing off it. This is the SINGLE load
    // chokepoint, so every entry point registers uniformly (→ A.19). The WASM
    // in-browser parser that used to handle the no-jobId path was deleted in the
    // pdf-editor-no-itext Epic Phase 4 — there is no client-side fallback, so a
    // registration failure aborts the load with a toast.
    if (!state.jobId) {
        try {
            showLoading("Preparing document...");
            state.jobId = await registerTransientJob(file);
        }
        catch (err) {
            log.error("[editor] transient-job registration failed:", err);
            hideLoading();
            showEditorToast("Could not open this PDF for editing. Please try again, or run "
                + "it through the main remediation pipeline first.", "error");
            announce("Could not open the PDF. Try again or use the main pipeline.");
            state.lifecycle.transition(state.interop
                ? EditorLifecycleState.EMPTY_WARM
                : EditorLifecycleState.EMPTY_COLD);
            return;
        }
    }
    // Persist the file across navigations so the back-button restores it.
    // Skip the write when we're restoring from the same source (avoid
    // round-tripping the bytes back to IDB on every nav).
    if (!opts.skipPersist) {
        // Fire-and-forget; persistence is best-effort and shouldn't block load.
        /* eslint-disable promise/prefer-await-to-then -- fire-and-forget IDB write; must not block load path */
        storeOpenFile(file).catch((err) => {
            log.warn("[editor] could not persist file for navigation:", err);
        });
        /* eslint-enable promise/prefer-await-to-then */
    }
    // Loading-overlay stage progression:
    //   1. "Loading PDF…"         — file.arrayBuffer() read.
    //   2. "Parsing structure…"   — backend struct-tree fetch (`editor-ir`).
    //   3. "Rendering…"           — PDF.js page-1 render.
    showLoading("Loading PDF...");
    if (!state.interop) {
        // Every entry now has a jobId (registered above if it lacked one), so the
        // interop is always a BackendEditorInterop (fetch-based, IR-driven; no
        // WASM bundle, no Blazor bootstrap). The irProvider closure resolves
        // `state.ir` lazily, so the interop always reads the CURRENT (post-edit)
        // IR for its bbox source. Per retrofit design §2.2/§2.3.
        state.interop = await createEditorInterop({
            // Guaranteed non-null: the block above registers a transient job when
            // state.jobId is falsy (and returns on failure).
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- jobId ensured non-null above; createEditorInterop requires string (no WASM fallback)
            jobId: state.jobId,
            irProvider: () => state.ir,
        });
        setLoadingLabel("Loading PDF...");
    }
    const buf = await file.arrayBuffer();
    const bytes = new Uint8Array(buf);
    // Race pdf.js init with the backend-IR fetch. pdf.js's getDocument runs in
    // its own Web Worker against its own copy of the bytes, so it proceeds in
    // parallel with the network round-trip. Capturing the promise here (don't
    // await yet) overlaps pdf.js's 200-600 ms parse with the backend IR fetch.
    // We await this promise once we actually need state.pdfDoc.
    //
    // NB: pdfjsLib + GlobalWorkerOptions.workerSrc are configured at
    // module load (editor.ts top); they are guaranteed initialised by
    // the time loadFile runs. The orchestrator installs the bootstrapped
    // pdf.js bundle onto window.__editorPdfjs so this module can reach
    // it without re-running the top-level await.
    if (!window.__editorPdfjs) { // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        throw new Error("loadFile: window.__editorPdfjs is not initialised. " // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
            + "editor.ts must install the pdf.js bundle onto window before calling loadFile.");
    }
    const pdfDocPromise = window.__editorPdfjs.getDocument({ data: bytes }).promise; // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
    // Don't let an early pdf.js failure surface as an unhandled rejection
    // before we have a handler attached on the await below; tag a no-op
    // catch so the rejection is observed.
    // eslint-disable-next-line promise/prefer-await-to-then -- rejection-suppressor: promise is awaited below; .catch() here prevents unhandled-rejection before the await
    pdfDocPromise.catch(() => { });
    // AUTHORITATIVE IR (pdf-editor-no-itext Phase 2a+4): kick off the backend
    // `editor-ir` fetch here, in parallel with pdf.js. Every entry is jobId-keyed
    // (transient job registered above), so the backend IR is the authoritative
    // struct-tree the editor renders and edits (`state.ir`). Capturing the promise
    // now overlaps its network round-trip with the IR-derived struct seam below;
    // Tier-2 consumes the resolved result. Resolves to null only when the backend
    // is unreachable, which the struct-seam catch below treats as fatal.
    // Per docs/design/pdf-editor-backend-session-retrofit-260715.md §2.2.
    // parallel-overlap: the .catch() attaches the fallback handler at
    // promise-CREATION so this network round-trip overlaps the struct seam below;
    // the resolved result is awaited in Tier-2 (§2.2). Converting to await here
    // would serialize the fetch and defeat the overlap.
    const authoritativeIRPromise = loadAuthoritativeIR(state.jobId)
        // eslint-disable-next-line promise/prefer-await-to-then -- parallel-overlap creation-time fallback handler (see comment above); awaited in Tier-2
        .catch((err) => {
        log.warn("[editor] loadAuthoritativeIR failed; backend IR unavailable:", err);
        return null;
    });
    // Suppress unhandled-rejection before the Tier-2 await; already .catch()'d
    // above so this is defensive.
    // eslint-disable-next-line promise/prefer-await-to-then -- rejection-suppressor: promise is awaited in Tier-2; .catch() here prevents unhandled-rejection in the gap
    authoritativeIRPromise.catch(() => { });
    // ─── BACKEND-IR LOAD (pdf-editor-no-itext Phase 3+4, retrofit design
    // §2.2/§3) ─────────────────────────────────────────────────────────────
    // The BACKEND `editor-ir` fetch (kicked off above) is the AUTHORITATIVE
    // source of the struct-tree the editor renders + edits (`state.ir`) AND of
    // the accessibility attestation (`state.attestation`, from the IR meta —
    // Phase 2 folded `PdfAttestationReader.Read` into the IR).
    //
    // The `state.interop` calls below are the BackendEditorInterop's IR-derived
    // seam (Phase 4 deleted the WASM+iText in-browser parser). `loadPdf` /
    // `getStructTree` derive the overlay ROLE-MAP source (`state.structTree`)
    // from the backend IR and effectively never fail (they read the injected IR
    // and only throw the PdfNotLoaded guard). The remaining failure surface here
    // is the `withTimeout` timeout. A failure is NON-FATAL when the backend IR
    // loaded (the editor still renders + edits from `state.ir`); it is FATAL only
    // when the backend was unreachable, since there is no in-browser parser to
    // fall back to (the WASM path is gone).
    setLoadingLabel("Parsing structure...");
    // Kick attestation off in parallel with the struct-tree derive. When the
    // backend IR is authoritative, `state.attestation` is set from the IR meta in
    // Tier-2 (below), overwriting whatever this IR-derived read produced here.
    let attestationPromise = null;
    try {
        await withTimeout(state.interop.loadPdf(bytes, file.name), STRUCT_PARSE_TIMEOUT_MS, "PDF load");
        attestationPromise = state.interop.getAttestation();
        // Don't surface a rejection before we observe it below.
        // eslint-disable-next-line promise/prefer-await-to-then -- rejection-suppressor: promise is awaited below; .catch() here prevents unhandled-rejection before the await
        attestationPromise.catch(() => { });
        state.structTree = await withTimeout(state.interop.getStructTree(), STRUCT_PARSE_TIMEOUT_MS, "struct-tree parse");
        // Invalidate the role-map cache so renderOverlays rebuilds it from
        // the new struct tree.
        state.overlayRoleMap = null;
    }
    catch (err) {
        // The IR-derived struct-tree seam failed (in practice: a withTimeout
        // timeout). If the BACKEND IR is (or will be) authoritative, this is
        // NON-FATAL — the editor renders + edits from `state.ir`; only the
        // overlay geometry is degraded (no bboxes). Await the authoritative-IR
        // promise (already in flight since load start, so the happy path never
        // reaches here) to decide.
        const authoritative = await authoritativeIRPromise;
        if (authoritative) {
            // Backend IR is authoritative — degrade gracefully. Leave
            // `state.structTree` null (overlays clear themselves; the edit
            // surface is the card pane driven by `state.ir`). The load
            // continues into Tier-1 render + Tier-2 IR install below.
            log.warn("[editor] struct-tree derive failed but backend IR is authoritative; "
                + "continuing without overlay geometry:", err);
            state.structTree = null;
            state.overlayRoleMap = null;
        }
        else {
            // Backend IR was unreachable — there is no in-browser parser to fall
            // back to (the WASM path was deleted in Phase 4), so this is fatal.
            log.error("[editor] backend IR unreachable (no fallback):", err);
            const isTimeout = err !== null && typeof err === "object" && err.isTimeout === true;
            // WHY: load failures dismiss the loading overlay and surface the
            // error via toast so the user can drop a different file or open the
            // empty state — keeping the full-screen overlay would block all
            // interaction. The auto-open-from-URL path (where there's no editor
            // to fall back to) still uses showLoadingError — see the
            // autoOpenFromLocation() call site below.
            hideLoading();
            showEditorToast(isTimeout
                ? "Timed out loading this PDF. Try again, or run it through the main remediation pipeline first."
                : `Could not load PDF structure: ${err !== null && typeof err === "object" && err.message ? err.message : "unknown error"}`, "error");
            // Reset state so a subsequent file drop starts clean.
            state.pdfDoc = null;
            state.structTree = null;
            // Phase FB.9 (260524): route IR teardown through replaceIR so
            // subscribers observe the transition. Per §P.5 of
            // docs/design/frontend-mv-architecture-260524.md.
            replaceIR(null, "teardown");
            // Lifecycle: LOADING -> EMPTY_WARM (interop was built, so warm not
            // cold). On the cold path (interop is null because createEditorInterop
            // threw earlier), transition to EMPTY_COLD instead.
            state.lifecycle.transition(state.interop
                ? EditorLifecycleState.EMPTY_WARM
                : EditorLifecycleState.EMPTY_COLD);
            return;
        }
    }
    // Read attestation off the IR meta (via the interop's IR-derived
    // getAttestation). Tier-2 (below) re-sets this from the authoritative IR
    // meta once installed. The promise was kicked off in parallel with the
    // struct-tree derive above; this await usually resolves immediately. It is
    // null when the derive failed under an authoritative backend IR (the
    // degraded path above never set attestationPromise).
    try {
        state.attestation = attestationPromise ? await attestationPromise : null;
    }
    catch {
        // best-effort; attestation is non-critical on load
        state.attestation = null;
    }
    // Stage: rendering page via PDF.js. The getDocument call was kicked
    // off in parallel above; this await usually resolves immediately
    // because pdf.js's parse has been running concurrently with the
    // backend IR fetch.
    setLoadingLabel("Rendering...");
    state.pdfDoc = await pdfDocPromise;
    populateSidebar(cardPaneDeps.selectStruct);
    updatePageNavControls();
    showWorkspace(applyLayout);
    updateAttestationPill();
    // Reset to page 1 and fit-to-width scale on new file.
    // WHY showWorkspace() before renderPage(): the workspace is display:none until
    // shown, so els.pdfScroll.clientWidth returns 0 inside fitToWidthScale() if
    // renderPage() fires first — producing a 10%-zoom thumbnail instead of
    // fit-to-width.
    state.currentPage = 1;
    state.currentScale = NaN; // NaN = use fit-to-width
    // ─── Tier 1: load-blocking work (must complete before workspace is usable) ──
    // The user must see page 1 within ≤5 s of workspace-visible. The lazy-load
    // paradigm (docs/ux/editor-ux-redesign-260501.md §4 + the 260504 brief)
    // is strict: only the PDF binary + page-1 frame + page-1 canvas-paint
    // qualify as Tier 1. Everything else (later pages, IR, cards, overlays,
    // screen-reader panel) is Tier 2 — queued in the background after Tier 1
    // commits, never blocking hideLoading().
    //
    // mountMultiPageScroll builds N empty frames and wires the multi-page
    // manager; the per-page canvas/overlay renders are themselves Tier 2 (the
    // manager promotes pages on visibility, page 1 only at first paint).
    await mountMultiPageScroll();
    await renderPage(1);
    // Tier 1 complete — hide loading overlay so the user sees page 1
    // immediately. All remaining work runs as Tier 2 background tasks below.
    hideLoading();
    state.lifecycle.transition(EditorLifecycleState.LOADED_TIER1);
    const elapsed = (performance.now() - t0).toFixed(0);
    announce(`${file.name} loaded in ${elapsed} milliseconds.`);
    // structTree can be null on the Phase-3 degraded path (WASM geometry walk
    // failed but the backend IR is authoritative) — report -1 struct count in
    // that case rather than dereferencing null.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- WHY: pdfDoc assigned earlier in loadFile; guaranteed non-null by this point in the function
    log.info(`[editor] loadFile Tier-1 finished in ${elapsed} ms; pages=${state.pdfDoc.numPages}, structs=${state.structTree ? state.structTree.nodes.length : -1}`);
    // Background bbox prefetch (lazy-load redesign 260508 §3.2 phase 2):
    // warm pages 2..min(20, N) once Tier 1 has committed. Single WASM range
    // call populates the per-page cache for every page in the window so
    // subsequent scrolls into that range serve overlays from cache (~ms)
    // rather than triggering ~hundreds-of-ms full-doc walks. _scheduleIdle
    // wraps requestIdleCallback with a setTimeout fallback for Safari ≤15.
    const _totalPages = state.pdfDoc ? state.pdfDoc.numPages : 0;
    if (_totalPages > 1) {
        _scheduleIdle(() => {
            void prefetchRange(2, Math.min(20, _totalPages)); // fire-and-forget: idle prefetch; errors logged inside prefetchRange
        });
    }
    // ─── Tier 2: background fill-in (overlays, cards, IR, screen-reader) ───────
    // Schedule via setTimeout(0) so the browser commits the page-1 paint
    // before we start the heavy DOM-building (954 cards on the 60-page SSA
    // fixture is enough to block the event loop for hundreds of ms). Use
    // requestIdleCallback when available so we yield to user input.
    //
    // The authoritative backend-IR promise (kicked off in Tier-1, above) is
    // forwarded so Tier-2 installs `state.ir` from the backend parse — the
    // WASM struct list is only the gated fallback (Phase 2a inversion).
    _scheduleTier2Loading(t0, cardPaneDeps, authoritativeIRPromise);
}
/**
 * Tier 2 background loading: IR fetch, card-pane mount, screen-reader
 * panel, second overlay re-render (for badges).
 *
 * Runs AFTER Tier 1 (page-1 paint + hideLoading) so it never blocks the
 * page-1 visible-state signal.
 *
 * @param t0 performance.now() at loadFile entry, for elapsed-time logging.
 * @param cardPaneDeps orchestrator-owned `{selectStruct, syncMcidToggleUI}`
 *   bundle forwarded into `mountCardPane`. Phase EB.5 introduced these
 *   per-mount injections to retire the prior orchestrator-scope setters.
 * @param authoritativeIRPromise the backend-IR fetch kicked off in Tier-1
 *   (pdf-editor-no-itext Phase 2a). Resolves to `{ document, source }` when
 *   the backend IR is authoritative, or null when there is no jobId / the
 *   backend was unreachable (the WASM struct list is then the fallback).
 */
export function _scheduleTier2Loading(t0, cardPaneDeps, authoritativeIRPromise = Promise.resolve(null)) {
    // Capture the lifecycle generation BEFORE scheduling the idle work.
    // If the user resets the editor between now and the idle callback's
    // completion, generation will have been bumped (entering EMPTY_WARM
    // increments it). The callback re-checks before mutating state and
    // bails out, so a late-arriving Tier-2 result cannot land partial
    // state on top of a fresh empty-state. See the regression test
    // `Tier-2 race: late-arriving result after reset is rejected via
    // generation check` in editor-lifecycle-state.test.mjs.
    const gen0 = state.lifecycle.generation;
    // Pane-local Tier-2 loading indicator (§3.3): a fresh controller for
    // THIS Tier-2 run, built synchronously (cheap DOM-node creation) before
    // scheduling the idle work so a stale generation's captured reference
    // never observes another run's node. The controller stays HIDDEN until
    // the first generation check below passes — labels are set only from
    // the winning generation's chain, never before (§3.3.4c).
    const paneOverlay = _resetCardPaneOverlay();
    _scheduleIdle(() => {
        void (async () => {
            try {
                // The authoritative backend IR was kicked off in Tier-1 and runs in
                // parallel with pdf.js + the WASM geometry walk; await its resolved
                // result here. `authoritative` is null when there is no jobId
                // (drag-drop) or the backend was unreachable — the WASM struct list
                // is then the gated fallback (Phase 2a inversion). Per retrofit
                // design §2.2.
                const authoritative = await authoritativeIRPromise;
                const irFromServer = authoritative?.document ?? null;
                // Race-check: did the user reset between scheduling and now?
                // The generation counter is bumped on entry to EMPTY_COLD /
                // EMPTY_WARM. If it differs, a reset has fired and we must
                // not promote — the IR/cards/SR-panel install would land
                // on top of a stale state.cards/state.ir from a different file.
                if (state.lifecycle.generation !== gen0
                    || !state.lifecycle.is(EditorLifecycleState.LOADED_TIER1)) {
                    log.info("[editor] Tier-2 idle callback aborting: lifecycle changed "
                        + `(gen0=${gen0}, now=${state.lifecycle.generation}, state=${state.lifecycle.state})`);
                    return;
                }
                // Pane-local indicator ON — this generation won the race above,
                // so it (and only it) is allowed to drive the overlay. The
                // Preview pane (`els.panePreview`) is never touched by this
                // controller, so it stays interactive (P-7/P-8).
                paneOverlay.setState(LoadingOverlayState.LOADING, {
                    label: PANE_LOADING_LABELS["fetch-oplog"],
                });
                // F7 Stage 5: prefer the IDB IR snapshot for fast boot. The
                // snapshot was written on the most recent successful save; if
                // it exists it reflects the last server-acknowledged state.
                // Server-fetched IR wins on tie (it's authoritative across
                // devices); IDB snapshot is the fallback when offline.
                //
                // Parallelize the three independent boot reads: IDB snapshot,
                // IDB local-ops, and the server oplog fetch (which can be a
                // 6-30s GCS cold-read). The oplog URL depends on the cursor
                // from the snapshot, so the oplog promise chains on snapshot;
                // local-ops is truly independent. Promise.all overlaps the
                // local-ops IDB read with the oplog network fetch.
                /* eslint-disable promise/prefer-await-to-then -- all .then()/.catch() below are Promise construction patterns (error-to-null transforms) used to build promises for Promise.all; inlining as await would break the parallel fetch strategy */
                const editorDbReady = state.editorDb;
                const jobIdReady = state.jobId;
                const dbReady = editorDbReady !== null && jobIdReady !== null;
                const snapshotPromise = (dbReady && editorDbReady !== null && jobIdReady !== null)
                    ? loadIrSnapshot(editorDbReady, jobIdReady).catch((err) => {
                        log.warn("[editor] IDB loadIrSnapshot failed:", err);
                        return null;
                    })
                    : Promise.resolve(null);
                const localOpsPromise = (dbReady && editorDbReady !== null && jobIdReady !== null)
                    ? loadLocalOps(editorDbReady, jobIdReady).catch((err) => {
                        log.warn("[editor] IDB loadLocalOps failed:", err);
                        return null;
                    })
                    : Promise.resolve(null);
                const oplogPromise = (dbReady && jobIdReady !== null)
                    ? snapshotPromise.then(async (snap) => {
                        const cursor = (snap && typeof snap.lastSyncedOpSeq === "number"
                            && snap.lastSyncedSessionId)
                            ? { sessionId: snap.lastSyncedSessionId, opSeq: snap.lastSyncedOpSeq }
                            : { sessionId: null, opSeq: -1 };
                        const sinceSeq = (typeof cursor.opSeq === "number")
                            ? cursor.opSeq + 1 : 0;
                        let url = `/api/editor-oplog/${encodeURIComponent(jobIdReady)}`
                            + `?since_seq=${sinceSeq}`;
                        if (cursor.sessionId) {
                            url += `&since_session=${encodeURIComponent(cursor.sessionId)}`;
                        }
                        url = appendTokenIfPresent(url);
                        return fetch(url, {
                            headers: { Accept: "application/x-ndjson" },
                        });
                    }).catch((err) => {
                        log.warn("[editor] boot oplog fetch failed:", err);
                        return null;
                    })
                    : Promise.resolve(null);
                /* eslint-enable promise/prefer-await-to-then */
                const [snap, local, oplogResp] = await Promise.all([
                    snapshotPromise, localOpsPromise, oplogPromise,
                ]);
                // Re-check lifecycle after the parallel reads — any of them
                // could have taken long enough for a reset to fire.
                if (state.lifecycle.generation !== gen0
                    || !state.lifecycle.is(EditorLifecycleState.LOADED_TIER1)) {
                    log.info("[editor] Tier-2 idle callback aborting after parallel reads: "
                        + `lifecycle changed (gen0=${gen0}, now=${state.lifecycle.generation}, `
                        + `state=${state.lifecycle.state})`);
                    // Defensive cleanup: don't leave a superseded run's overlay
                    // in LOADING. The NEW generation's own _scheduleTier2Loading
                    // call already reset the shared DOM node independently
                    // (§3.3.4a); this just avoids a visible flicker if this
                    // bail-out interleaves with that reset.
                    if (!paneOverlay.is(LoadingOverlayState.HIDDEN)) {
                        paneOverlay.setState(LoadingOverlayState.HIDDEN);
                    }
                    return;
                }
                // Apply snapshot first: sets state.lastSyncedCursor and
                // (potentially) irFromIdb, in the same order as the previous
                // sequential code.
                let irFromIdb = null;
                // WHY: IrSnapshot.ir is typed as `unknown` (wire-deserialized); we
                // know the stored value is always a Document (written by storeIrSnapshot
                // with a valid IR). Assert the type at this known-safe load boundary.
                if (snap?.ir)
                    irFromIdb = snap.ir;
                if (snap && typeof snap.lastSyncedOpSeq === "number"
                    && snap.lastSyncedSessionId) {
                    state.lastSyncedCursor = {
                        sessionId: snap.lastSyncedSessionId,
                        opSeq: snap.lastSyncedOpSeq,
                    };
                }
                // Phase FB.9 (260524): boot IR install routes through replaceIR
                // so subscribers (statusbar, attestation pill, etc.) observe the
                // doc-level + every-page transition. Per §P.5.
                //
                // AUTHORITATIVE-IR ordering (pdf-editor-no-itext Phase 2a+4): the
                // backend IR (`irFromServer`) wins — it is the authoritative
                // struct-tree the editor renders/edits. The IDB snapshot is the
                // offline fallback; `buildIRFromStructTree(structTree.nodes)` is the
                // last-resort defensive fallback for the backend-unreachable case
                // (the WASM in-browser parser it once bridged is gone).
                // `authoritative.source` names which parse won so the load path is
                // observable. Per retrofit design §2.2.
                const authoritativeSource = authoritative?.source
                    ?? (irFromIdb ? "idb-snapshot" : "struct-tree-fallback");
                // Phase-3 load inversion (retrofit design §2.2/§2.4): when the
                // backend IR is authoritative, source `state.attestation` from the
                // IR meta (Phase 2 folded PdfAttestationReader.Read into the IR)
                // rather than the Tier-1 IR-derived getAttestation read.
                if (irFromServer) {
                    state.attestation = irFromServer.attestation ?? null;
                    // Repaint the toolbar pill: Tier-1 painted it from the Tier-1
                    // read (or null on the degraded path); the authoritative IR
                    // meta value may differ.
                    updateAttestationPill();
                }
                // structTree is null on the degraded path (the IR-derived struct
                // seam failed under an authoritative backend IR). The backend IR
                // (`irFromServer`) or IDB snapshot then supplies the boot IR; the
                // `buildIRFromStructTree` fallback only runs when both are absent,
                // which cannot co-occur with a null structTree.
                const bootIR = (irFromServer ?? irFromIdb)
                    ?? buildIRFromStructTree(state.structTree ? state.structTree.nodes : []);
                log.info(`[editor] loadFile Tier-2 IR source=${authoritativeSource}; blocks=${bootIR.blocks?.length ?? 0}`);
                replaceIR(bootIR, "boot");
                // F7 Stage 5: pull server oplog tail past the cursor, merge with
                // any local PENDING ops, replay the deltas onto state.ir. Memo
                // §5.1 — server records win on conflict (already-acknowledged);
                // local-pending records are the "we crashed before save" case
                // and are re-applied on top.
                if (dbReady && oplogResp?.ok) {
                    try {
                        const text = await oplogResp.text();
                        const serverOps = parseJsonlBody(text);
                        const merged = mergeServerOplog(local ?? [], serverOps);
                        const _opCount = merged.newFromServer.length + merged.localPending.length;
                        paneOverlay.setLabel(_opCount > 0
                            ? `Applying ${_opCount} edit${_opCount === 1 ? "" : "s"}…`
                            : PANE_LOADING_LABELS["replay-ops"]);
                        // Replay server-only deltas first (memo §5.1.4a),
                        // then local-pending. dispatch() throws IRError on
                        // ill-formed ops; we skip-with-log so a single bad
                        // record doesn't poison the boot.
                        //
                        // Phase FB.9 (260524): replay routes through dispatch()
                        // so blocksByPage rebuilds per-op (pre-FB.9 the direct
                        // raw assignment bypassed the index rebuild, leaving
                        // boot-replay subscribers with a stale index until the
                        // FB.8 one-shot rebuildBlocksByPage call at line ~645
                        // ran). Subscribers aren't registered yet during boot,
                        // so the per-op notify is a no-op pre-mount.
                        for (const r of merged.newFromServer) {
                            try {
                                dispatch(stripPersistenceFields(r));
                            }
                            catch (err) {
                                log.warn("[editor] replay server op failed:", err);
                            }
                        }
                        for (const r of merged.localPending) {
                            try {
                                dispatch(stripPersistenceFields(r));
                            }
                            catch (err) {
                                log.warn("[editor] replay local-pending op failed:", err);
                            }
                        }
                        // Persist merged IR snapshot so the next reload
                        // picks up the post-replay state immediately.
                        if (state.ir && editorDbReady !== null && jobIdReady !== null) {
                            /* eslint-disable promise/prefer-await-to-then -- fire-and-forget IDB write; intentionally non-blocking so oplog replay doesn't stall boot */
                            storeIrSnapshot(editorDbReady, jobIdReady, {
                                ir: state.ir,
                                savedAt: new Date().toISOString(),
                                lastSyncedOpSeq: state.lastSyncedCursor.opSeq,
                                lastSyncedSessionId: state.lastSyncedCursor.sessionId ?? undefined,
                                currentSessionId: state.currentSessionId,
                            }).catch((err) => {
                                log.warn("[editor] IDB storeIrSnapshot (boot) failed:", err);
                            });
                            /* eslint-enable promise/prefer-await-to-then */
                        }
                    }
                    catch (err) {
                        log.warn("[editor] boot oplog replay failed:", err);
                    }
                }
                // Phase FB.8 (260524): rebuild the per-page block index once, after
                // boot oplog replay has settled. Subsequent dispatches will rebuild
                // it on every non-doc-level op via the dispatcher's commit path.
                rebuildBlocksByPage(state.ir);
                // BACKEND-INTEROP structTree refresh (retrofit design §2.3): on the
                // backend path the overlay ROLE-MAP source is the IR (the WASM struct
                // walk is gone). The Tier-1 `getStructTree()` ran before the IR was
                // installed, so `state.structTree` is empty. Now that the IR is
                // materialized (post oplog-replay), re-fetch the IR-derived struct
                // tree from the interop seam and invalidate the role-map cache so the
                // Tier-2 `renderOverlays()` below rebuilds roles from the IR. No-op on
                // the no-jobId WASM fallback path (WASM getStructTree already ran and
                // `irFromServer` is null there). `state.structTree` may be null on the
                // degraded path (WASM walk failed under authoritative IR) — the
                // interop refresh restores it from the IR.
                if (state.jobId && state.interop && state.ir) {
                    try {
                        state.structTree = await state.interop.getStructTree();
                        state.overlayRoleMap = null;
                    }
                    catch (err) {
                        log.warn("[editor] backend structTree refresh failed:", err);
                    }
                }
                if (!state.ir?.blocks || state.ir.blocks.length === 0) {
                    showEditorToast("This PDF has no accessibility structure yet. "
                        + "You can hand-add elements with the type picker, or remediate it via the main page first.", "info");
                }
                state.opLog = new OpLog();
                paneOverlay.setLabel(PANE_LOADING_LABELS["build-cards"]);
                // Card list is the primary edit surface. The Cluster F card
                // pane takes the orchestrator-owned `selectStruct` +
                // `syncMcidToggleUI` callbacks via the deps bundle plumbed
                // through `loadFile`'s parameter; no module-private setter.
                mountCardPane(cardPaneDeps);
                // Tier-2 ready — the item list is built and visible; clear the
                // pane-local indicator (P-8). Direct LOADING -> HIDDEN is a
                // legal transition (no fade, unlike the page-global hideLoading
                // — MVP simplicity; a fade is a cheap boyscout follow-up).
                paneOverlay.setState(LoadingOverlayState.HIDDEN);
                // Mount the screen-reader slide-over in the edit pane.
                mountScreenReaderPanel();
                // mountCardPane → rebuildCardLookups populated state.readingOrderByStructId,
                // but the first overlay paint (renderPage(1)/multi-page manager) ran
                // BEFORE the lookup table existed, so the order badges never drew.
                // Re-render overlays now that the lookup table exists — this is
                // what paints the order-badge chips on the page-1 overlay boxes.
                await renderOverlays().catch((err) => {
                    log.warn("[editor] overlay reorder badge re-render failed:", err);
                });
                // LOADED_TIER1 -> LOADED_FULL fires window.__editorPdfReady=true
                // via the on_enter hook on state.lifecycle.
                state.lifecycle.transition(EditorLifecycleState.LOADED_FULL);
                const elapsed2 = (performance.now() - t0).toFixed(0);
                log.info(`[editor] loadFile Tier-2 finished in ${elapsed2} ms`);
            }
            catch (err) {
                log.error("[editor] Tier-2 background load failed:", err);
                // Surface the failure on the pane-local overlay (§3.3.2 "ERROR
                // with the existing retry affordance on a Tier-2 fatal") — but
                // only if this generation ever showed it (HIDDEN -> ERROR is
                // not a legal transition; a failure before the first generation
                // check above never touched the overlay, so there's nothing to
                // surface here).
                if (!paneOverlay.is(LoadingOverlayState.HIDDEN)) {
                    paneOverlay.setState(LoadingOverlayState.ERROR, {
                        label: "Couldn't load the item list. Reload to try again.",
                    });
                }
            }
        })();
    });
}
